/**
 * UUIDv7 generation.
 *
 * The terminal creates identifiers for everything it can create offline, and
 * those identifiers become the primary keys on the server. That is what makes
 * every push idempotent: re-sending a sale collides on the key and is a no-op.
 *
 * v7 rather than v4 because the leading 48 bits are a millisecond timestamp,
 * so keys sort chronologically and Postgres index pages stay dense instead of
 * fragmenting across random inserts.
 */
const getRandomValues = (bytes) => {
    const c = globalThis.crypto;
    if (!c?.getRandomValues) {
        throw new Error('a cryptographic random source is required to mint identifiers');
    }
    return c.getRandomValues(bytes);
};
const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));
/** Monotonic guard so two calls in the same millisecond still order correctly. */
let lastMs = 0;
let lastSeq = 0;
const SEQ_MAX = 0xfff;
export function uuidv7(now = Date.now()) {
    // Never emit a key that sorts before one already handed out. A terminal that
    // loses power and comes back with a bad RTC must not mint keys that collide
    // with, or sort behind, sales it has already recorded.
    let ms = Math.max(now, lastMs);
    if (ms === lastMs) {
        lastSeq += 1;
        // The 12-bit sequence is full for this millisecond. Borrow from the next
        // one rather than letting the counter wrap, which would break ordering.
        if (lastSeq > SEQ_MAX) {
            ms = lastMs + 1;
            lastSeq = 0;
        }
    }
    else {
        lastSeq = 0;
    }
    lastMs = ms;
    const bytes = new Uint8Array(16);
    getRandomValues(bytes);
    bytes[0] = (ms / 2 ** 40) & 0xff;
    bytes[1] = (ms / 2 ** 32) & 0xff;
    bytes[2] = (ms / 2 ** 24) & 0xff;
    bytes[3] = (ms / 2 ** 16) & 0xff;
    bytes[4] = (ms / 2 ** 8) & 0xff;
    bytes[5] = ms & 0xff;
    // version 7 in the high nibble of byte 6, sequence in the remaining 12 bits
    bytes[6] = 0x70 | ((lastSeq >> 8) & 0x0f);
    bytes[7] = lastSeq & 0xff;
    // RFC 4122 variant
    bytes[8] = 0x80 | (bytes[8] & 0x3f);
    const h = HEX;
    return (h[bytes[0]] + h[bytes[1]] + h[bytes[2]] + h[bytes[3]] + '-' +
        h[bytes[4]] + h[bytes[5]] + '-' +
        h[bytes[6]] + h[bytes[7]] + '-' +
        h[bytes[8]] + h[bytes[9]] + '-' +
        h[bytes[10]] + h[bytes[11]] + h[bytes[12]] + h[bytes[13]] +
        h[bytes[14]] + h[bytes[15]]);
}
/** Extract the embedded timestamp. Handy in tests and in diagnostics. */
export function uuidv7Time(id) {
    const hex = id.replace(/-/g, '').slice(0, 12);
    return new Date(Number.parseInt(hex, 16));
}
/**
 * Human-facing sale folio. Scoped per terminal, so two offline terminals can
 * both be issuing folios without coordinating and without colliding.
 * Shape: T<terminal short>-YYMMDD-NNNN
 */
export function buildFolio(terminalCode, date, sequence) {
    const yy = String(date.getFullYear()).slice(2);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${terminalCode}-${yy}${mm}${dd}-${String(sequence).padStart(4, '0')}`;
}
//# sourceMappingURL=ids.js.map